import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Twitter, Mail, MapPin, Phone } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_LOGO = "https://customer-assets.emergentagent.com/job_pooch-crew/artifacts/401fwdfd_Logo%202-main.jpg";

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/api/')) {
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  }
  return url;
};

export const Footer = () => {
  const [logoUrl, setLogoUrl] = useState(DEFAULT_LOGO);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        if (response.data?.logo_image) {
          setLogoUrl(getImageUrl(response.data.logo_image));
        }
      } catch (error) {
        console.log('Using default logo');
      }
    };
    fetchSettings();
  }, []);

  const footerLinks = {
    shop: [
      { name: 'Petwear', path: '/shop?category=petwear' },
      { name: 'People Wear', path: '/shop?category=peoplewear' },
      { name: 'Matching Sets', path: '/shop?category=matching' },
      { name: 'New Arrivals', path: '/shop' },
    ],
    company: [
      { name: 'About Us', path: '/about' },
      { name: 'Contact', path: '/contact' },
      { name: 'Size Guide', path: '/size-guide' },
      { name: 'FAQ', path: '/faq' },
    ],
    support: [
      { name: 'Shipping Info', path: '/shipping' },
      { name: 'Returns', path: '/returns' },
      { name: 'Privacy Policy', path: '/privacy' },
      { name: 'Terms of Service', path: '/terms' },
    ],
  };

  const socialLinks = [
    { icon: Instagram, href: '#', label: 'Instagram' },
    { icon: Facebook, href: '#', label: 'Facebook' },
    { icon: Twitter, href: '#', label: 'Twitter' },
  ];

  return (
    <footer data-testid="footer" className="bg-[#f5f0e6] border-t border-[#c68b4d]/20">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <img
                src={logoUrl}
                alt="PoochCrew Logo"
                className="h-14 w-14 rounded-full object-cover shadow-md"
              />
              <span className="font-heading text-2xl font-bold text-[#1e3a5f]">
                PoochCrew
              </span>
            </Link>
            <p className="font-body text-base leading-relaxed text-[#1e3a5f]/70 mb-6 max-w-sm">
              Premium matching apparel for you and your best friend. Because every moment together deserves to be stylish.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-[#1e3a5f]/70">
                <Mail className="w-4 h-4 text-[#c68b4d]" />
                <span className="font-body text-sm">anil@poochcrew.com</span>
              </div>
              <div className="flex items-center gap-3 text-[#1e3a5f]/70">
                <Phone className="w-4 h-4 text-[#c68b4d]" />
                <span className="font-body text-sm">1(647)293-4022</span>
              </div>
              <div className="flex items-center gap-3 text-[#1e3a5f]/70">
                <MapPin className="w-4 h-4 text-[#c68b4d]" />
                <span className="font-body text-sm">Ontario, Canada</span>
              </div>
            </div>
          </div>

          {/* Shop Links */}
          <div>
            <h4 className="font-heading text-lg font-semibold text-[#1e3a5f] mb-4">
              Shop
            </h4>
            <ul className="space-y-3">
              {footerLinks.shop.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="font-body text-sm text-[#1e3a5f]/70 hover:text-[#e76f51] transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-heading text-lg font-semibold text-[#1e3a5f] mb-4">
              Company
            </h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="font-body text-sm text-[#1e3a5f]/70 hover:text-[#e76f51] transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h4 className="font-heading text-lg font-semibold text-[#1e3a5f] mb-4">
              Support
            </h4>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="font-body text-sm text-[#1e3a5f]/70 hover:text-[#e76f51] transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-[#c68b4d]/20">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            {/* Copyright */}
            <p className="font-body text-sm text-[#1e3a5f]/60">
              © {currentYear} PoochCrew. All rights reserved.
            </p>

            {/* Social Links */}
            <div className="flex items-center gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  data-testid={`social-${social.label.toLowerCase()}`}
                  className="w-10 h-10 rounded-full bg-[#1e3a5f]/10 flex items-center justify-center text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white transition-all"
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
